import {
  PremiumBenefitList,
  SETTINGS_PREMIUM_SUPERWALL_PLACEMENT,
  useUpgradeToPremium,
} from "@-/dev.improve.haniverse.components.superwall";
import { Button } from "@-/dev.improve.haniverse.components.ui.button";
import { ScreenReadyMarker } from "@-/dev.improve.haniverse.components.ui.screen-ready-marker";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { PrimaryNavigationStripFadeInsetContext } from "@-/dev.improve.haniverse.lib.primary-navigation-strip";
import { useDeviceSetting } from "@-/dev.improve.haniverse.lib.device-settings";
import { ScrollView, View } from "@-/com.npmjs.react-native";
import { useContext, useEffect, useRef } from "react";

/**
 * What a FREE learner sees once they have cleared every review and there is nothing left to
 * learn inside the free tier.
 *
 * This state only exists because the lesson resolver is capped at the free-tier levels
 * for free users (see `use-get-next-lesson-query`). Before that cap the learner was handed
 * lessons past the free tier that the free-tier gate refused at the reveal step — a paywall presented as a
 * dead end rather than as the finish line it actually is.
 *
 * Deliberately NOT the same thing as the paywall itself: the paywall is Superwall's, and it opens
 * from the button here. This screen is the congratulation plus the case for upgrading, which is
 * why it enumerates every premium feature rather than pitching one.
 */
export function LearnSummaryFreeTierCompleteView() {
  const bottomTabFadeInset = useContext(PrimaryNavigationStripFadeInsetContext);
  // The explicit-upgrade placement, shared with Settings and the Browse levels prompt. The
  // learn-gate placement is a different campaign and may have no paywall assigned to it.
  const { errorMessage, isLoading, isPaywallPresented, openUpgradeToPremium } = useUpgradeToPremium(
    SETTINGS_PREMIUM_SUPERWALL_PLACEMENT,
    "summary_free_tier_complete",
  );
  const [hasAutoPresented, setHasAutoPresented] = useDeviceSetting(
    "hasAutoPresentedFreeTierPaywall",
  );
  // The FIRST time a learner reaches the boundary, the paywall opens by itself — that is the
  // moment the pitch lands, and it is the one interruption this flow gets. Every later arrival
  // just shows this screen, whose button opens the same paywall on demand.
  //
  // The ref guards the window between `openUpgradeToPremium()` and the MMKV write becoming
  // visible: `openUpgradeToPremium` is not referentially stable (it closes over the placement
  // hook's state), so without it a re-render inside that window would present twice.
  const hasRequestedRef = useRef(false);
  useEffect(() => {
    if (hasAutoPresented || hasRequestedRef.current) {
      return;
    }
    hasRequestedRef.current = true;
    setHasAutoPresented(true);
    openUpgradeToPremium();
  }, [hasAutoPresented, openUpgradeToPremium, setHasAutoPresented]);

  return (
    <View className="min-h-0 flex-1 bg-muted">
      <ScreenReadyMarker screen="learn-summary-free-tier-complete" />
      <ScrollView
        className="min-h-0 flex-1"
        contentContainerClassName="grow gap-6 px-5 pt-6"
        contentContainerStyle={{ paddingBottom: bottomTabFadeInset + 32 }}
      >
        <View className="items-center gap-2" testID="learn-free-tier-complete-heading">
          <Text className="text-center font-sans text-[28px] font-semibold" colorVariant="default">
            You&rsquo;re all caught up
          </Text>
          <Text className="text-center font-sans text-[17px]" colorVariant="muted">
            {
              "You've finished every review and every HSK 1 level. That's the whole free tier — nicely done."
            }
          </Text>
        </View>

        <View className="gap-3 rounded-xs border border-border bg-surface p-4">
          <Text className="font-sans text-[17px] font-semibold" colorVariant="default">
            Here&rsquo;s what Premium unlocks
          </Text>
          <PremiumBenefitList />
        </View>

        <View className="gap-2">
          <Button
            accessibilityLabel="Upgrade to Premium to unlock all levels"
            disabled={isPaywallPresented}
            isLoading={isLoading}
            onPress={openUpgradeToPremium}
            testID="learn-free-tier-complete-upgrade-button"
          >
            <Text colorVariant="inverse">{isLoading ? "Loading plans…" : "Unlock all levels"}</Text>
          </Button>
          {errorMessage === null ? null : (
            <Text
              accessibilityRole="alert"
              className="text-center font-sans text-[13px]"
              colorVariant="muted"
            >
              {errorMessage}
            </Text>
          )}
          <Text className="text-center font-sans text-[13px]" colorVariant="muted">
            Your existing cards keep coming back for review either way.
          </Text>
        </View>
      </ScrollView>
    </View>
  );
}
